import { TbPointer } from "solid-icons/tb";
import { type Component, type JSX, type ParentComponent } from "solid-js";
import type { Scene } from "~/hooks/useScene";

export type Person = { name: string; color: string };

/** Seconds into the scene, as the inline animation delay a staged element starts at. */
export const at = (seconds: number): JSX.CSSProperties => ({ "animation-delay": `${seconds}s` });

/**
 * The root of every mockup: one image to assistive tech, described by `label`,
 * and the element whose armed / in-view classes run the CSS choreography. It
 * takes no pointer input, so the scroll boxes inside the shared views it embeds
 * can't catch the wheel and hold the page still.
 */
export const MockScene: ParentComponent<{ scene: Scene; label: string; class?: string }> = (props) => (
  <div
    ref={props.scene.ref}
    role="img"
    aria-label={props.label}
    class={`mk-scene pointer-events-none text-left select-none ${props.class ?? ""}`}
    classList={{ "mk-armed": props.scene.armed(), "in-view": props.scene.inView() }}
  >
    {props.children}
  </div>
);

/** The section heading both apps set over a list: small, uppercase, muted. */
export const PaneLabel: ParentComponent<{ class?: string; style?: JSX.CSSProperties }> = (props) => (
  <p
    class={`text-[11px] font-semibold tracking-wide text-muted-text uppercase ${props.class ?? ""}`}
    style={props.style}
  >
    {props.children}
  </p>
);

export const Crown: Component<{ class?: string }> = (props) => (
  <img src="/images/chief-logo-gold-crown-only.svg" alt="" class={`shrink-0 object-contain ${props.class ?? ""}`} />
);

/**
 * A pointer that glides onto its parent's centre and clicks about 1.1s after
 * `start`. The parent must be positioned.
 */
export const Cursor: Component<{ start: number }> = (props) => (
  <span class="cursor-path pointer-events-none absolute top-1/2 left-1/2 z-10" style={at(props.start)}>
    <TbPointer class="size-6 fill-primary-text text-tile drop-shadow" aria-hidden="true" />
  </span>
);
